import { useEffect, useState } from 'react';

import { useSharedServiceState } from '@getvim/internal-vim-os-sdk/react';
import { PatientSourceVaultToken } from '@getvim/patient-lookup-service/types';
import { orderAssistLogger } from '../../../logger';
import { getPatientInsurance } from '../../../../../utils/insurer.utils';
import { exportAnalyticsClient } from '../../../../../analytics/clients/exportAnalytics.client';
import { referralRequestAnalyticsClient } from '../../../../../analytics/referral-request';
import { analyticsManager } from '../../../../../analytics/analyticsManager';
import { vimPatientIdsManager } from '../../../../../utils/vimPatientIdsManager';
import { AppPatient } from '../../../types';
import { Patient } from '../../../types/entities';

export function usePatientLookupSharedService(
  ehrPatient: (Patient & { token?: string }) | undefined,
): AppPatient | undefined {
  const serviceState = useSharedServiceState();

  const [patient, setPatient] = useState<AppPatient>();

  const [patientLookupResponse, setPatientLookupResponse] = useState<{
    patientSourceVaultTokens: PatientSourceVaultToken[];
    patientToken: string;
  }>();
  useEffect(() => {
    const { 'patient-lookup-service': patientLookupService } = serviceState;
    if (patientLookupService) {
      const { patientLookupResponse: patientLookupServiceRespose, serviceInit } =
        patientLookupService.customMessage.payload;
      if (serviceInit) {
        orderAssistLogger.info('Received patient-lookup-service init event', {
          instanceId: serviceInit.instanceId,
        });
        return;
      }
      if (patientLookupServiceRespose.patientToken !== patientLookupResponse?.patientToken) {
        setPatientLookupResponse(patientLookupServiceRespose);
      } else {
        orderAssistLogger.info(
          'Received new patient-lookup-service event for same patientToken. skipping',
        );
      }
    }
  }, [serviceState]);

  useEffect(() => {
    if (
      ehrPatient &&
      patientLookupResponse &&
      patientLookupResponse.patientToken === ehrPatient.token
    ) {
      orderAssistLogger.info('Initialize AppPatient with patientSourceVaultTokens', {
        patientToken: ehrPatient.token,
      });

      const patientInsurance = getPatientInsurance(
        ehrPatient.insurance,
        patientLookupResponse.patientSourceVaultTokens,
      );

      setPatient({
        ...ehrPatient,
        patientSourceVaultTokens: patientLookupResponse.patientSourceVaultTokens,
        insurance: patientInsurance,
      });

      // Handle analytics baseProperties
      analyticsManager.setPatientBaseProperties({
        vimPatientId: ehrPatient.vimPatientId || null,
        ehrInsurance: patientInsurance?.ehrInsurance || null,
        insurer: patientInsurance?.insurer || null,
      });

      exportAnalyticsClient.setPatientBaseProperties({
        vimPatientId: ehrPatient.vimPatientId || null,
        ehrInsurance: patientInsurance?.ehrInsurance || null,
        insurer: patientInsurance?.insurer || null,
      });

      referralRequestAnalyticsClient.setPatientBaseProperties({
        vimPatientId: ehrPatient.vimPatientId || null,
        ehrInsurance: patientInsurance?.ehrInsurance || null,
        insurer: patientInsurance?.insurer || null,
      });

      vimPatientIdsManager.handleUpdateIds(
        patientLookupResponse.patientSourceVaultTokens,
      );
    } else {
      orderAssistLogger.info(
        'Reset AppPatient- ehrPatient and patientSourceVaultTokens are not matched for same patient',
        {
          ehrPatientToken: ehrPatient?.token,
          patientLookupToken: patientLookupResponse?.patientToken,
        },
      );

      setPatient(undefined);
      vimPatientIdsManager.resetIds();
    }
  }, [patientLookupResponse, ehrPatient]);

  return patient;
}
